import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import type { DeviceDetail, DeviceListResponse } from '@/types';

interface DeviceFilters {
  [key: string]: string | number | undefined;
  page?: number;
  limit?: number;
  search?: string;
  site?: string;
  deviceType?: string;
  hasActiveTask?: string;
  driftSeverity?: string;
  dataQualityMin?: number;
  identityConfidence?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

/**
 * Hook to fetch paginated device list
 */
export function useDevices(filters: DeviceFilters = {}) {
  return useQuery({
    queryKey: queryKeys.devices.list(filters),
    queryFn: async () => {
      const response = await api.get<DeviceListResponse>(endpoints.devices.list, {
        params: {
          page: filters.page || 1,
          limit: filters.limit || 25,
          search: filters.search,
          site: filters.site,
          deviceType: filters.deviceType,
          hasActiveTask: filters.hasActiveTask,
          driftSeverity: filters.driftSeverity,
          dataQualityMin: filters.dataQualityMin,
          identityConfidence: filters.identityConfidence,
          sort: filters.sort || 'hostname',
          order: filters.order || 'asc',
        },
      });
      return response;
    },
  });
}

/**
 * Hook to fetch single device details
 */
export function useDevice(id: string | null) {
  return useQuery({
    queryKey: queryKeys.devices.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Device ID required');
      // API returns { data: DeviceDetail }, so we unwrap it
      const response = await api.get<{ data: DeviceDetail }>(endpoints.devices.get(id));
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook to trigger OCS rescan for a device
 */
export function useTriggerRescan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { deviceId: string; agentId?: string }) => {
      const body = params.agentId ? { agent_id: params.agentId } : undefined;
      return api.post(`${endpoints.devices.get(params.deviceId)}/rescan`, body);
    },
    onSuccess: (_, params) => {
      // Invalidate device queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.detail(params.deviceId) });
    },
  });
}

/**
 * Device update payload
 */
export interface DeviceUpdatePayload {
  ipAddress?: string | null;
  hostname?: string;
  notes?: string | null;
  primaryMacAddress?: string | null;
  siteId?: string | null;
  roomId?: string | null;
}

/**
 * Hook to update device fields
 */
export function useUpdateDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, data }: { deviceId: string; data: DeviceUpdatePayload }) => {
      return api.patch<{
        success: boolean;
        message: string;
        updatedFields: string[];
        device: {
          id: string;
          hostname: string;
          ipAddress: string | null;
          primaryMacAddress: string | null;
          site: string | null;
          siteId: string | null;
          room: string | null;
          roomId: string | null;
        };
      }>(endpoints.devices.get(deviceId), data);
    },
    onSuccess: (_, { deviceId }) => {
      // Invalidate device queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

/**
 * Software item with compliance status
 */
export interface SoftwareItem {
  id: string;
  name: string;
  version: string | null;
  publisher: string | null;
  installDate: string | null;
  status: 'allowed' | 'dangerous' | 'unknown';
  severity: 'low' | 'medium' | 'high' | 'critical' | null;
  reason: string | null;
  category: string | null;
}

export interface DeviceSoftwareResponse {
  deviceId: string;
  hostname: string;
  software: SoftwareItem[];
  summary: {
    total: number;
    allowed: number;
    dangerous: number;
    unknown: number;
  };
  lastUpdated: string | null;
}

/**
 * Hook to fetch device software with compliance status
 */
export function useDeviceSoftware(deviceId: string | null) {
  return useQuery({
    queryKey: ['devices', 'software', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID required');
      return api.get<DeviceSoftwareResponse>(`${endpoints.devices.get(deviceId)}/software`);
    },
    enabled: !!deviceId,
  });
}

/**
 * Remediation options response
 */
export interface RemediationOptions {
  device: {
    id: string;
    hostname: string;
    fogHostId: number | null;
  };
  software: {
    id: string;
    name: string;
    publisher: string | null;
    version: string | null;
  };
  options: {
    downloadScript: {
      available: boolean;
      formats: string[];
      description: string;
    };
    deployRemotely: {
      available: boolean;
      description: string;
    };
  };
}

/**
 * Hook to get remediation options for a software item
 */
export function useRemediationOptions(deviceId: string | null, softwareId: string | null) {
  return useQuery({
    queryKey: ['remediation', 'options', deviceId, softwareId],
    queryFn: async () => {
      if (!deviceId || !softwareId) throw new Error('Device ID and Software ID required');
      return api.get<RemediationOptions>(`/api/v1/remediation/options/${deviceId}/${softwareId}`);
    },
    enabled: !!deviceId && !!softwareId,
  });
}

/**
 * Hook to download remediation script
 */
export function useDownloadRemediationScript() {
  return useMutation({
    mutationFn: async ({ softwareName, publisher, format }: {
      softwareName: string;
      publisher?: string | null;
      format: 'powershell' | 'batch';
    }) => {
      const response = await fetch('/api/v1/remediation/script', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ softwareName, publisher, format }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate script');
      }

      const blob = await response.blob();
      const filename = format === 'powershell'
        ? `FogPilot_Remove_${softwareName.replace(/[^a-zA-Z0-9]/g, '_')}.ps1`
        : `FogPilot_Remove_${softwareName.replace(/[^a-zA-Z0-9]/g, '_')}.bat`;

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true, filename };
    },
  });
}

/**
 * Hook to deploy remediation via FOG Snapin
 */
export function useDeployRemediation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, softwareName, publisher }: {
      deviceId: string;
      softwareName: string;
      publisher?: string | null;
    }) => {
      return api.post<{
        success: boolean;
        message: string;
        snapin: { id: number; name: string };
        device: { id: string; hostname: string; fogHostId: number };
        note: string;
      }>('/api/v1/remediation/deploy', { deviceId, softwareName, publisher });
    },
    onSuccess: (_, variables) => {
      // Invalidate software queries to refresh after remediation
      queryClient.invalidateQueries({ queryKey: ['devices', 'software', variables.deviceId] });
    },
  });
}

/**
 * Hook to set/override software status (allowed, dangerous, unknown)
 */
export function useSetSoftwareStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ softwareName, publisher, status }: {
      softwareName: string;
      publisher?: string | null;
      status: 'allowed' | 'dangerous' | 'unknown';
    }) => {
      return api.post<{
        success: boolean;
        message: string;
      }>('/api/v1/software/override', { softwareName, publisher, status });
    },
    onSuccess: () => {
      // Invalidate all software queries to refresh status across all devices
      queryClient.invalidateQueries({ queryKey: ['devices', 'software'] });
    },
  });
}

/**
 * Drift change event from API
 */
export interface DriftChangeEvent {
  id: string;
  eventType: string;
  changeCategory: string;
  severity: 'low' | 'medium' | 'high';
  changedAt: string;
  fields: Record<string, { old: unknown; new: unknown }>;
  detectedBy: string;
}

/**
 * Device drift response from API
 */
export interface DeviceDriftResponse {
  deviceId: string;
  hostname: string;
  drift: {
    severity: 'none' | 'low' | 'medium' | 'high';
    counts: {
      high: number;
      medium: number;
      low: number;
      total: number;
    };
    recentEvents: DriftChangeEvent[];
  };
}

/**
 * Hook to fetch device drift detection details
 */
export function useDeviceDrift(deviceId: string | null) {
  return useQuery({
    queryKey: ['devices', 'drift', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID required');
      return api.get<DeviceDriftResponse>(`${endpoints.devices.get(deviceId)}/drift`);
    },
    enabled: !!deviceId,
  });
}
